import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListDocumentsInput } from "@nexxt/contracts/documents";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the documents (50-api/documents.md, ADR-118): one factory, the same keys for the loader, the
// screens and the relaunch.

const documents = api.documents.documents;

type ListPage = InferResponseType<typeof documents.$get, 200>;
export type DocumentSummary = ListPage["items"][number];
export type DocumentDetail = InferResponseType<(typeof documents)[":documentId"]["$get"], 200>;

/** The filters of the list input (`listDocumentsInput`), without the paging the queries add. */
export type DocumentFilters = Pick<ListDocumentsInput, "status" | "type" | "customer" | "q"> & {
  overdue?: true | undefined;
  retryable?: true | undefined;
};

const PAGE_SIZE = 50;

const keys = {
  all: ["documents"] as const,
  /** Prefix of every cached list: a relaunch puts the document it gets back in each of them. */
  lists: ["documents", "list"] as const,
};

export const documentQueries = {
  ...keys,

  list: (filters: DocumentFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          documents.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** The documents of a sales order: a few for each shipment, one page. */
  ofSalesOrder: (salesOrderId: string) =>
    queryOptions({
      queryKey: [...keys.all, "of", salesOrderId],
      queryFn: async ({ signal }) =>
        (await unwrap(documents.$get({ query: { salesOrder: salesOrderId, pageSize: "100" } }, { init: { signal } })))
          .items,
    }),

  /** A document with every time it was asked for. */
  detail: (documentId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", documentId],
      queryFn: ({ signal }) => unwrap(documents[":documentId"].$get({ param: { documentId } }, { init: { signal } })),
    }),
};
